import React, { useCallback, useEffect, useState } from 'react';
import {
  X, Loader2, Send, Sparkles, ArrowUp, ArrowDown, Trash2, Facebook,
  Instagram, MessageCircle, Linkedin, Star,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ShareCenter from './ShareCenter';
import TikTokComposer from './TikTokComposer';

const CHANNELS = [
  { id: 'facebook', label: 'Facebook', Icon: Facebook, color: 'text-blue-600' },
  { id: 'instagram', label: 'Instagram', Icon: Instagram, color: 'text-pink-600' },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, color: 'text-sky-700' },
  { id: 'telegram', label: 'Telegram', Icon: Send, color: 'text-cyan-600' },
  { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, color: 'text-green-600' },
];

export default function DistributionPreviewModal({ propertyId, onClose }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [variant, setVariant] = useState('A');
  const [images, setImages] = useState([]);          // [{url, watermarked}]
  const [copies, setCopies] = useState({});          // {channel: text} for current variant
  const [enabled, setEnabled] = useState({});        // {channel: bool}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.previewDistribution(propertyId);
      setData(res);
      setImages(res.images || []);
      const ch = {};
      CHANNELS.forEach((c) => { ch[c.id] = res.channels?.[c.id] !== false; });
      setEnabled(ch);
      setCopies({ ...(res.copies?.A || {}) });
      setVariant('A');
    } catch (e) {
      toast({ title: 'Error al generar vista previa', description: String(e.message || e), variant: 'destructive' });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const switchVariant = (v) => {
    setVariant(v);
    setCopies({ ...(data?.copies?.[v] || {}) });
  };

  const moveImg = (i, dir) => {
    setImages((arr) => {
      const next = [...arr];
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const removeImg = (i) => setImages((arr) => arr.filter((_, idx) => idx !== i));

  const publish = async () => {
    const channels = CHANNELS.filter((c) => enabled[c.id]).map((c) => c.id);
    if (channels.length === 0) {
      toast({ title: 'Selecciona al menos una red', variant: 'destructive' });
      return;
    }
    setPublishing(true);
    try {
      const res = await api.dispatchDistribution(propertyId, {
        variant,
        copies,
        images: images.map((im) => im.url),
        channels,
      });
      const sim = (res.items || []).filter((i) => i.mode === 'dry_run').length;
      toast({ title: 'Distribución ejecutada', description: `${res.total} canal(es) · ${sim} simulado(s) · variante ${variant}.` });
      onClose();
    } catch (e) {
      toast({ title: 'Error al distribuir', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="dist-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-habita" />
            <h3 className="font-bold text-habita-dark">Vista previa · antes de publicar</h3>
          </div>
          <button onClick={onClose} data-testid="dist-preview-close" className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-16 text-center" data-testid="dist-preview-loading">
            <Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" />
            <p className="text-sm text-gray-500 mt-3">La IA está eligiendo la portada y redactando el copy…</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Imágenes */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-habita-dark text-sm">Carrusel ({images.length}/10) · marca de agua incluida</h4>
                {data?.image_source === 'ai' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-habita/10 text-habita font-bold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> orden por IA
                  </span>
                )}
              </div>
              {images.length === 0 ? (
                <p className="text-sm text-gray-500">Este inmueble no tiene imágenes.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dist-preview-images">
                  {images.map((im, i) => (
                    <div key={im.url + i} className="relative group rounded-xl overflow-hidden border border-gray-200" data-testid={`dist-preview-img-${i}`}>
                      <img src={im.watermarked || im.url} alt="" className="w-full h-24 object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-habita text-white font-bold flex items-center gap-0.5">
                          <Star className="w-2.5 h-2.5" /> Portada
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity py-1">
                        <button onClick={() => moveImg(i, -1)} disabled={i === 0} className="p-1 rounded bg-white/90 disabled:opacity-30" data-testid={`dist-img-up-${i}`}><ArrowUp className="w-3 h-3" /></button>
                        <button onClick={() => moveImg(i, 1)} disabled={i === images.length - 1} className="p-1 rounded bg-white/90 disabled:opacity-30" data-testid={`dist-img-down-${i}`}><ArrowDown className="w-3 h-3" /></button>
                        <button onClick={() => removeImg(i)} className="p-1 rounded bg-white/90 text-rose-600" data-testid={`dist-img-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Variante A/B + redes */}
            <section className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">Variante A/B:</span>
                {['A', 'B'].map((v) => (
                  <button
                    key={v}
                    data-testid={`dist-variant-${v}`}
                    onClick={() => switchVariant(v)}
                    className={`w-9 h-9 rounded-full font-bold text-sm border-2 ${variant === v ? 'border-habita bg-habita text-white' : 'border-gray-200 text-gray-500'}`}
                  >{v}</button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {CHANNELS.map(({ id, label, Icon, color }) => (
                  <button
                    key={id}
                    data-testid={`dist-preview-channel-${id}`}
                    onClick={() => setEnabled((e) => ({ ...e, [id]: !e[id] }))}
                    className={`px-2.5 h-8 rounded-lg border-2 text-xs font-semibold flex items-center gap-1.5 ${enabled[id] ? 'border-habita bg-habita/5' : 'border-gray-200 opacity-50'}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
                  </button>
                ))}
              </div>
            </section>

            {/* Copy editable por red */}
            <section className="space-y-3">
              <h4 className="font-semibold text-habita-dark text-sm">Copy por red (editable) · variante {variant}</h4>
              {CHANNELS.filter((c) => enabled[c.id]).map(({ id, label, Icon, color }) => (
                <div key={id}>
                  <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
                  </label>
                  <textarea
                    data-testid={`dist-copy-${id}`}
                    value={copies[id] || ''}
                    onChange={(e) => setCopies((c) => ({ ...c, [id]: e.target.value }))}
                    rows={id === 'facebook' || id === 'whatsapp' ? 5 : 3}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm whitespace-pre-wrap"
                  />
                </div>
              ))}
            </section>

            {/* Share Center (modo manual reutilizable) */}
            <ShareCenter propertyId={propertyId} variant={variant} />

            {/* TikTok (desacoplado, sandbox) */}
            <TikTokComposer propertyId={propertyId} />
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
            <p className="text-xs text-gray-400">El enlace corto <code className="bg-gray-100 px-1 rounded">{data?.short_url}</code> registra los clics para el A/B.</p>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-4 h-10 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancelar</button>
              <button
                onClick={publish}
                disabled={publishing}
                data-testid="dist-preview-publish"
                className="px-5 h-10 rounded-xl bg-habita text-white font-semibold text-sm flex items-center gap-2 hover:bg-habita-dark disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publicar ahora
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
